import { useNavigate, useLoaderData, useParams, type LoaderFunctionArgs } from 'react-router-dom';
import { Modal, StatusBadge } from '@immo/ui-kit';
import type { EmailTemplate, ErrorCode } from '@immo/api-contract';
import { EmailTemplateMarkup } from '@immo/components';
import { apiClient } from '@/lib/auth/api-client';
import { requireUuidParam } from '@/lib/routing/require-uuid-param';
import { useTranslations } from '@/i18n/use-translations';
import { savedByLabel, usePlaceholderLabel, useUpdatedAtLabel } from '@/pages/workspaces/email-templates-section';

type WorkspaceEmailTemplateData =
  { readonly kind: 'loaded'; readonly template: EmailTemplate } | { readonly kind: 'not-found' };

const NOT_FOUND: ErrorCode = 'identity.email-template.not-found';

export async function workspaceEmailTemplateLoader({
  params,
}: LoaderFunctionArgs): Promise<WorkspaceEmailTemplateData> {
  const workspaceId = requireUuidParam(params, 'id');
  const emailTemplateId = requireUuidParam(params, 'emailTemplateId');
  const result = await apiClient.adminEmailTemplates.get({ pathParams: { workspaceId, emailTemplateId } });

  if (result.ok) return { kind: 'loaded', template: result.body };

  const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

  if (result.status === 404 && code === NOT_FOUND) return { kind: 'not-found' };

  throw new Response('Failed to load e-mail template', { status: result.status });
}

function DefRow({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-text-primary">{children}</dd>
    </>
  );
}

export function WorkspaceEmailTemplateModal() {
  const t = useTranslations('pages.admin.workspaces.email-templates');
  const tRoot = useTranslations();
  const tErrors = useTranslations('errors');
  const navigate = useNavigate();
  const { id: workspaceId } = useParams();
  const data = useLoaderData() as WorkspaceEmailTemplateData;
  const placeholderLabel = usePlaceholderLabel();
  const updatedAtLabel = useUpdatedAtLabel();

  const close = () => navigate(`/workspaces/${workspaceId}`);

  if (data.kind === 'not-found') {
    return (
      <Modal open size="lg" confirmOnClose={false} onClose={close} title={t('title')}>
        <p className="text-base text-text-secondary">{tErrors('identity.email-template.not-found')}</p>
      </Modal>
    );
  }

  const { template } = data;

  return (
    <Modal open size="lg" confirmOnClose={false} onClose={close} title={template.name}>
      <div className="space-y-4">
        <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
          <DefRow label={t('columns.area')}>
            <StatusBadge size="sm" tone="neutral" label={tRoot(`common.email-templates.areas.${template.area}`)} />
          </DefRow>
          <DefRow label={t('columns.updated-at')}>{updatedAtLabel(template.updatedAt)}</DefRow>
          <DefRow label={t('columns.updated-by')}>{savedByLabel(template, t('immoos'))}</DefRow>
        </dl>
        <div className="space-y-4 rounded-md border border-border bg-surface p-4">
          <div>
            <h3 className="text-base-sm font-semibold text-text-primary">{t('subject-label')}</h3>
            <EmailTemplateMarkup markup={template.subject} placeholderLabel={placeholderLabel} className="mt-1" />
          </div>
          <EmailTemplateMarkup markup={template.body} placeholderLabel={placeholderLabel} />
        </div>
      </div>
    </Modal>
  );
}
